/**
 * CreditsPricingGuide.jsx — Premium Institutional Display
 *
 * Modal officiel "Guide des Crédits ESS" affiché dans les wallets Membre/Cooper/Expert.
 * Récupère la configuration depuis /api/credits-guide (CMS Super Admin).
 * Accepte un `previewConfig` pour le mode aperçu admin.
 *
 * Design System O'SCOP Premium — Archetype Jewel & Luxury (Institutional Variant)
 *   - Header bleu marine #0a1e3f + accents or #fbbf24
 *   - Polices : Playfair Display (titres) · Outfit (tabs) · IBM Plex Sans (corps)
 *   - Micro-animations Tailwind (no extra deps)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Info, HelpCircle, Coins, Calculator, FileText, Mail, MessageSquare, Download, Share2, RefreshCw, FolderOpen, ChevronDown, Zap, Clock, Gift, Star, CheckCircle, XCircle, Sparkles } from 'lucide-react';
import { getToken } from '../services/tokenService';

const ICONS = {
  Coins, Calculator, FileText, Mail, MessageSquare, Download, Share2, RefreshCw,
  FolderOpen, Zap, Clock, Gift, Star, Info, HelpCircle, CheckCircle,
};

const PALETTE = {
  blue:    { ring: 'ring-blue-200',    chip: 'bg-blue-50 text-blue-700 border-blue-200',     icon: 'text-blue-700',    bar: 'bg-blue-500' },
  emerald: { ring: 'ring-emerald-200', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: 'text-emerald-700', bar: 'bg-emerald-500' },
  purple:  { ring: 'ring-purple-200',  chip: 'bg-purple-50 text-purple-700 border-purple-200', icon: 'text-purple-700',  bar: 'bg-purple-500' },
  amber:   { ring: 'ring-amber-200',   chip: 'bg-amber-50 text-amber-700 border-amber-200',   icon: 'text-amber-700',   bar: 'bg-amber-500' },
  rose:    { ring: 'ring-rose-200',    chip: 'bg-rose-50 text-rose-700 border-rose-200',     icon: 'text-rose-700',    bar: 'bg-rose-500' },
  slate:   { ring: 'ring-slate-200',   chip: 'bg-slate-50 text-slate-700 border-slate-200',  icon: 'text-slate-700',   bar: 'bg-slate-500' },
  indigo:  { ring: 'ring-indigo-200',  chip: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: 'text-indigo-700',  bar: 'bg-indigo-500' },
  cyan:    { ring: 'ring-cyan-200',    chip: 'bg-cyan-50 text-cyan-700 border-cyan-200',     icon: 'text-cyan-700',    bar: 'bg-cyan-500' },
};

const FONTS = {
  playfair: { fontFamily: '"Playfair Display", Georgia, serif' },
  outfit: { fontFamily: '"Outfit", system-ui, sans-serif' },
  body: { fontFamily: '"IBM Plex Sans", system-ui, sans-serif' },
};

const FALLBACK_CONFIG = {
  header: {
    title: 'Guide des Crédits ESS',
    subtitle: 'Comprendre et utiliser vos UC efficacement',
    color_from: '#0a1e3f',
    color_to: '#0B3C5D',
  },
  free_actions: [
    { label: 'Navigation / Consultation', description: 'Parcourir les menus et visualiser les données' },
    { label: 'Création de brouillon', description: 'Sauvegarder un travail en cours' },
    { label: 'Prévisualisation', description: 'Voir un aperçu avant validation' },
    { label: 'Historique personnel', description: 'Consulter ses propres transactions' },
  ],
  categories: [],
  how_it_works: [],
  credit_types: [],
};

// Fallback hardcodé exporté pour `useAutoCredits` (lookup local du coût uniquement)
export const CREDIT_PRICING = {
  simulation: { label: 'Simulations & Calculs', actions: [
    { code: 'SIM_STANDARD', credits: 2 }, { code: 'SIM_ADVANCED', credits: 3 },
    { code: 'SIM_SPECIAL', credits: 4 }, { code: 'SIM_RECALC', credits: 1 },
  ]},
  document: { label: 'Documents & PDF', actions: [
    { code: 'DOC_STANDARD', credits: 2 }, { code: 'DOC_CUSTOM', credits: 4 },
    { code: 'DOC_AMENDMENT', credits: 3 }, { code: 'DOC_PACK', credits: 6 },
  ]},
  partage: { label: 'Partage & Téléchargement', actions: [
    { code: 'SHARE_SECURE', credits: 1 }, { code: 'DOWNLOAD_SINGLE', credits: 1 },
    { code: 'DOWNLOAD_PACK', credits: 2 }, { code: 'REISSUE', credits: 1 },
  ]},
  messagerie: { label: 'Messagerie & Communication', actions: [
    { code: 'MSG_SHORT', credits: 1 }, { code: 'MSG_STRUCTURED', credits: 2 }, { code: 'MSG_ARCHIVED', credits: 3 },
  ]},
  expert: { label: 'Production Expert', actions: [
    { code: 'EXPERT_REPORT_SIMPLE', credits: 6 }, { code: 'EXPERT_REPORT_TECHNICAL', credits: 10 },
    { code: 'EXPERT_REPORT_LONG', credits: 15 }, { code: 'EXPERT_LETTER_SIMPLE', credits: 3 },
    { code: 'EXPERT_LETTER_ARGUMENTED', credits: 5 }, { code: 'EXPERT_LETTER_PROBANT', credits: 7 },
  ]},
};

// ============================================
// SKELETON Loading
// ============================================
const GuideSkeleton = () => (
  <div className="p-6 space-y-4 animate-pulse" data-testid="credits-guide-skeleton">
    <div className="h-20 bg-slate-100 rounded-xl" />
    <div className="h-16 bg-slate-100 rounded-lg" />
    <div className="h-16 bg-slate-100 rounded-lg" />
    <div className="h-16 bg-slate-100 rounded-lg" />
  </div>
);

// ============================================
// CATEGORY ACCORDION (premium, smooth)
// ============================================
const CategoryAccordion = ({ category, isOpen, onToggle }) => {
  const IconComp = ICONS[category.icon] || Coins;
  const pal = PALETTE[category.color] || PALETTE.blue;
  const visibleActions = (category.actions || []).filter((a) => a.visible !== false);
  const totalCredits = visibleActions.reduce((s, a) => s + (a.credits || 0), 0);

  return (
    <div
      className={`group border border-slate-200 rounded-xl overflow-hidden transition-all duration-300 ${isOpen ? 'shadow-md ring-1 ' + pal.ring : 'hover:shadow-sm hover:-translate-y-px'}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-white hover:bg-slate-50/80 transition-colors"
        data-testid={`category-${category.key}`}
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className={`flex-shrink-0 w-11 h-11 rounded-lg border ${pal.chip} flex items-center justify-center`}>
            <IconComp className={`h-5 w-5 ${pal.icon}`} />
          </div>
          <div className="text-left min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-slate-900 truncate" style={FONTS.outfit}>{category.label}</h4>
              {category.expert_only && (
                <Badge className="text-[10px] uppercase tracking-wider bg-rose-700 text-white border-0 font-semibold">Expert</Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5" style={FONTS.body}>
              {visibleActions.length} action{visibleActions.length > 1 ? 's' : ''} · {totalCredits} UC max
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="hidden sm:inline text-xs text-slate-400 uppercase tracking-widest" style={FONTS.outfit}>
            {isOpen ? 'Réduire' : 'Détails'}
          </span>
          <div className={`w-7 h-7 rounded-full bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
            <ChevronDown className="h-4 w-4 text-slate-600" />
          </div>
        </div>
      </button>

      <div
        className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
      >
        <div className="overflow-hidden">
          <div className="bg-gradient-to-b from-slate-50 to-white border-t border-slate-100 divide-y divide-slate-100">
            {visibleActions.map((action) => (
              <div key={action.code} className="flex items-start justify-between gap-4 px-4 py-3 hover:bg-white transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${pal.bar}`} />
                    <p className="font-medium text-slate-900 text-sm" style={FONTS.body}>{action.label}</p>
                  </div>
                  {action.description && (
                    <p className="text-xs text-slate-500 mt-1 ml-3.5" style={FONTS.body}>{action.description}</p>
                  )}
                </div>
                <div className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0a1e3f] text-white">
                  <Coins className="h-3.5 w-3.5 text-[#fbbf24]" />
                  <span className="font-bold text-sm tabular-nums" style={FONTS.outfit}>{action.credits}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const CreditsPricingGuide = ({ userRole = 'member', compact = false, onClose, previewConfig = null }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('pricing');
  const [config, setConfig] = useState(previewConfig || FALLBACK_CONFIG);
  const [loading, setLoading] = useState(!previewConfig && !compact);

  useEffect(() => {
    if (previewConfig) {
      setConfig(previewConfig);
      setLoading(false);
      return;
    }
    if (compact) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/credits-guide`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setConfig(data);
      } catch {
        // fallback gracieux
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [previewConfig, compact]);

  const header = config.header || FALLBACK_CONFIG.header;
  const freeActions = config.free_actions || [];
  const categories = useMemo(() => (
    (config.categories || []).filter((c) => {
      if (c.expert_only && userRole !== 'expert') return false;
      return c.visible !== false;
    })
  ), [config.categories, userRole]);
  const howItWorks = config.how_it_works || [];
  const creditTypes = config.credit_types || [];

  // Compteurs hero
  const totalActions = useMemo(() => categories.reduce((s, c) => s + (c.actions?.filter((a) => a.visible !== false).length || 0), 0), [categories]);

  if (compact) {
    return (
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2 mb-1">
            <Info className="h-4 w-4" />Comment fonctionnent les UC ?
          </h4>
          <p className="text-xs text-blue-700">Les UC sont débités automatiquement lors des actions productives. La consultation reste gratuite.</p>
        </CardContent>
      </Card>
    );
  }

  const headerGradient = `linear-gradient(135deg, ${header.color_from || '#0a1e3f'} 0%, ${header.color_to || '#0B3C5D'} 100%)`;

  return (
    <div className="w-full max-w-4xl mx-auto bg-white rounded-2xl overflow-hidden shadow-2xl ring-1 ring-slate-200" id="credits-guide" data-testid="credits-guide" style={FONTS.body}>
      {/* ============================== HEADER ============================== */}
      <div className="relative overflow-hidden text-white" style={{ background: headerGradient }}>
        {/* Decorative texture */}
        <div
          className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/8110032b-6f65-4eb8-8d34-03bf75c7208f/images/db31b02c126dfe97b8cdeb75e90c25edea21aea9b3d13ab744df87cb5ee0e7d0.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        {/* Gold accent top-line */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#fbbf24] to-transparent" />

        <div className="relative p-6 md:p-7">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 min-w-0">
              <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-lg">
                <Coins className="h-7 w-7 text-[#fbbf24]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#fbbf24]/90 font-medium mb-1" style={FONTS.outfit}>
                  O'SCOP · Unités de Crédits de Contribution
                </div>
                <h2 className="text-2xl md:text-3xl font-bold leading-tight tracking-tight" style={FONTS.playfair}>
                  {header.title}
                </h2>
                {header.subtitle && (
                  <p className="text-sm md:text-base text-white/80 mt-1 max-w-xl" style={FONTS.body}>
                    {header.subtitle}
                  </p>
                )}
              </div>
            </div>
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-white/80 hover:text-white hover:bg-white/10 -mr-2 -mt-1"
                data-testid="credits-guide-close"
              >
                ✕
              </Button>
            )}
          </div>

          {/* Quick stats */}
          {!loading && (
            <div className="mt-5 grid grid-cols-3 gap-3 max-w-md">
              <div className="rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-white/70" style={FONTS.outfit}>Catégories</div>
                <div className="text-xl font-bold text-white tabular-nums" style={FONTS.outfit}>{categories.length}</div>
              </div>
              <div className="rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-white/70" style={FONTS.outfit}>Actions</div>
                <div className="text-xl font-bold text-white tabular-nums" style={FONTS.outfit}>{totalActions}</div>
              </div>
              <div className="rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-white/70" style={FONTS.outfit}>Gratuit</div>
                <div className="text-xl font-bold text-[#fbbf24] tabular-nums flex items-center gap-1" style={FONTS.outfit}>
                  {freeActions.length}<Sparkles className="h-4 w-4" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Gold accent bottom-line */}
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#fbbf24]/60 to-transparent" />
      </div>

      {/* ============================== BODY ============================== */}
      {loading ? (
        <GuideSkeleton />
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="px-4 md:px-6 pt-5 bg-white">
            <TabsList className="grid grid-cols-3 w-full h-auto p-1 bg-slate-100/80 rounded-xl" style={FONTS.outfit}>
              <TabsTrigger
                value="pricing"
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[#0a1e3f] text-slate-600 rounded-lg py-2 text-sm font-medium transition-all"
              >
                <Calculator className="h-4 w-4 mr-2" />Grille Tarifaire
              </TabsTrigger>
              <TabsTrigger
                value="how"
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[#0a1e3f] text-slate-600 rounded-lg py-2 text-sm font-medium transition-all"
              >
                <HelpCircle className="h-4 w-4 mr-2" />Comment ça marche
              </TabsTrigger>
              <TabsTrigger
                value="types"
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[#0a1e3f] text-slate-600 rounded-lg py-2 text-sm font-medium transition-all"
              >
                <Gift className="h-4 w-4 mr-2" />Types de Crédits
              </TabsTrigger>
            </TabsList>
          </div>

          {/* GRILLE TARIFAIRE */}
          <TabsContent value="pricing" className="px-4 md:px-6 py-5 space-y-5 mt-0 animate-in fade-in duration-300">
            {freeActions.length > 0 && (
              <div className="relative rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-emerald-100/40 blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="h-5 w-5 text-emerald-600" />
                    <h3 className="font-semibold text-emerald-900" style={FONTS.outfit}>
                      Actions Gratuites <span className="text-emerald-600 font-normal">· 0 UC</span>
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {freeActions.map((a, i) => (
                      <div key={i} className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-white/80 transition-colors">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5">
                          <CheckCircle className="h-3 w-3 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-emerald-950">{a.label}</p>
                          {a.description && <p className="text-xs text-emerald-700/80 mt-0.5">{a.description}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2.5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs uppercase tracking-[0.2em] font-medium text-slate-500" style={FONTS.outfit}>
                  Actions productives par catégorie
                </h3>
                <span className="text-xs text-slate-400">{categories.length} catégorie{categories.length > 1 ? 's' : ''}</span>
              </div>
              {categories.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl">
                  <FolderOpen className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Aucune catégorie configurée pour cet espace.</p>
                </div>
              ) : (
                categories.map((cat) => (
                  <CategoryAccordion
                    key={cat.key}
                    category={cat}
                    isOpen={expandedCategory === cat.key}
                    onToggle={() => setExpandedCategory(expandedCategory === cat.key ? null : cat.key)}
                  />
                ))
              )}
            </div>
          </TabsContent>

          {/* COMMENT ÇA MARCHE */}
          <TabsContent value="how" className="px-4 md:px-6 py-5 space-y-4 mt-0 animate-in fade-in duration-300">
            <div className="relative rounded-xl bg-gradient-to-br from-[#0a1e3f] to-[#0B3C5D] text-white p-5 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-[#fbbf24]/10 blur-3xl" />
              <div className="relative flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#fbbf24]/20 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-[#fbbf24]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1" style={FONTS.outfit}>Principe de base</h3>
                  <p className="text-sm text-white/85" style={FONTS.body}>
                    Les UC sont débités <strong className="text-[#fbbf24]">automatiquement</strong> au moment où vous effectuez une action productive. La consultation reste entièrement gratuite.
                  </p>
                </div>
              </div>
            </div>

            {howItWorks.length > 0 ? (
              <div className="space-y-3">
                <h3 className="text-xs uppercase tracking-[0.2em] font-medium text-slate-500 mt-2" style={FONTS.outfit}>
                  Parcours utilisateur
                </h3>
                <ol className="relative space-y-3 pl-6 border-l-2 border-dashed border-slate-200 ml-3">
                  {howItWorks.map((step, idx) => (
                    <li key={idx} className="relative">
                      <div className="absolute -left-[1.95rem] top-0 w-8 h-8 rounded-full bg-white border-2 border-[#0a1e3f] flex items-center justify-center shadow-sm">
                        <span className="text-xs font-bold text-[#0a1e3f] tabular-nums" style={FONTS.outfit}>{idx + 1}</span>
                      </div>
                      <div className="p-4 rounded-lg bg-white border border-slate-200 hover:shadow-md hover:-translate-y-px transition-all duration-300">
                        <div className="font-semibold text-slate-900" style={FONTS.outfit}>{step.title}</div>
                        {step.description && <p className="text-sm text-slate-600 mt-1">{step.description}</p>}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">Aucune étape configurée.</p>
            )}

            <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-rose-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-rose-900" style={FONTS.outfit}>Solde insuffisant ?</h3>
                  <p className="text-sm text-rose-800/90 mt-1">
                    L&apos;action sera bloquée et vous serez invité à recharger vos UC. Aucune donnée n&apos;est perdue — l&apos;action reprend exactement où elle s&apos;est arrêtée après rechargement.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* TYPES DE CRÉDITS */}
          <TabsContent value="types" className="px-4 md:px-6 py-5 space-y-3 mt-0 animate-in fade-in duration-300">
            {creditTypes.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl">
                <Gift className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Aucun type d'UC configuré.</p>
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {creditTypes.map((t) => {
                  const pal = PALETTE[t.color] || PALETTE.blue;
                  return (
                    <div
                      key={t.key}
                      className="group relative rounded-xl border border-slate-200 bg-white p-4 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
                    >
                      <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full ${pal.bar} opacity-5 group-hover:opacity-10 transition-opacity`} />
                      <div className="relative flex items-start gap-3">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl border ${pal.chip} flex items-center justify-center`}>
                          <Coins className={`h-6 w-6 ${pal.icon}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900" style={FONTS.outfit}>{t.label}</h4>
                          {t.description && <p className="text-sm text-slate-600 mt-1">{t.description}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-4 flex items-start gap-3">
              <Info className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-slate-600">
                Les UC temporaires (si présents) sont consommés en priorité, puis les bonus, puis les UC standard.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Footer mini */}
      <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between text-xs text-slate-500" style={FONTS.outfit}>
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span>Tarification officielle O'SCOP</span>
        </div>
        <span className="hidden sm:inline tracking-widest uppercase">Unités de Crédits de Contribution</span>
      </div>
    </div>
  );
};

export default CreditsPricingGuide;
